import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Button, DialogContent, DialogTitle, Typography, useMediaQuery } from '@mui/material';
import { IIntroductionType } from './introductionSteps.data';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ThemeStyle } from '../../module/ThemeManager';
import { theme } from '../../constants/theme';
import { LottieAnimation } from '../Animation/Animation';
import { arrowAnimationBlack } from '../../assets/animations';
import { useThemeContext } from '../../hook/useThemeManager';

export interface ITabSelector {
    onChange?: (selected: string[]) => void;
    step: IIntroductionType;
    themeStyle?: ThemeStyle;
    maxSelection?: number;
    minSelection?: number;
    onSelect?: (option: string) => void;
    skipRefresh?: boolean;
    shouldShowButton?: boolean;
    onClickButton?: () => void;
    disableButton?: boolean;
    maxSelectedDisableState?: boolean;
    initialValue?: string[];
    buttonText?: string;
    isStep?: boolean;
    tabsStyle?: React.CSSProperties;
    pillStyle?: React.CSSProperties;
    pillTextStyle?: React.CSSProperties;
    disableAnimation?: boolean;
}

const TabSelector: React.FC<ITabSelector> = ({
    step,
    isStep,
    onChange,
    // themeStyle,
    maxSelection,
    minSelection,
    onSelect,
    skipRefresh,
    shouldShowButton,
    onClickButton,
    disableButton,
    maxSelectedDisableState,
    initialValue,
    buttonText,
    tabsStyle,
    pillStyle,
    pillTextStyle,
    disableAnimation,
}) => {
    const [selected, setSelected] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [currentOptions, setCurrentOptions] = useState<string[]>([]);
    const optionsPerPage = 10;
    const start = currentPage * optionsPerPage;
    const end = start + optionsPerPage;
    const isMobile = useMediaQuery('(max-width: 600px)');
    const { themeStyle } = useThemeContext();

    const handleSelection = (option: string) => {
        const isSelected = isAlreadySelected(option);

        if (maxSelection === 1) {
            if (!isSelected) {
                setSelected([option]);
                onChange && onChange([option]);
            } else {
                setSelected([]);
                onChange && onChange([]);
            }
        } else {
            if (!isSelected && (!maxSelection || selected.length < maxSelection)) {
                const newSelected = [...selected, option];
                setSelected(newSelected);
                onChange && onChange(newSelected);
            } else {
                const filtered = selected.filter(filter => filter !== option);
                setSelected(filtered);
                onChange && onChange(filtered);
            }
        }
    };

    const nextButtonStyle = isMobile
        ? { bottom: 20, left: '50%', transform: 'translateX(-50%)' }
        : { right: 20, top: '50%', transform: 'translateY(-50%)' };

    const prevButtonStyle = isMobile
        ? { top: 20, left: '50%', transform: 'translateX(-50%)' }
        : { left: 20, top: '50%', transform: 'translateY(-50%)' };

    useEffect(() => {
        if (!skipRefresh) {
            setSelected([]);
        }

        if (initialValue) {
            setSelected(initialValue);
        }
    }, [step, skipRefresh, initialValue, themeStyle]);

    useEffect(() => {
        setCurrentOptions(step.options!.slice(start, end));
    }, [currentPage, end, start, step.options]);

    const isAlreadySelected = (option: string) => selected.some(selection => selection === option);

    return (
        <AnimatePresence mode="wait">
            <motion.div
                initial={disableAnimation ? false : { opacity: 0 }}
                animate={disableAnimation ? false : { opacity: 1 }}
                exit={
                    disableAnimation
                        ? undefined
                        : {
                              opacity: 0,
                              transition: { ease: 'easeIn', duration: 1.0 },
                          }
                }
                key={step.displayTitle}
                style={{ maxWidth: '550px' }}
                transition={disableAnimation ? undefined : { duration: 3.5 }}>
                <DialogTitle
                    style={{
                        fontFamily: FONT_ENUM.REGULAR_HEADER,
                        color: theme[themeStyle].primary_white_orange,
                        ...(isStep && { color: theme[themeStyle].tag_white }),
                    }}
                    sx={{
                        fontSize: {
                            xs: 24,
                            sm: 32,
                        },
                    }}>
                    {step.displayTitle}
                </DialogTitle>
                <DialogContent style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', ...tabsStyle }}>
                    {currentOptions &&
                        currentOptions.map((option, index) => (
                            <motion.div
                                key={index}
                                initial={disableAnimation ? false : { opacity: 0, scale: 0 }}
                                animate={disableAnimation ? false : { opacity: 1, scale: 1 }}
                                onClick={() => handleSelection(option)}
                                transition={disableAnimation ? undefined : { delay: index * 0.2, duration: 0.5 }}
                                style={{ margin: '5px', cursor: 'pointer', ...pillStyle }}>
                                <Typography
                                    sx={{
                                        paddingX: 2,
                                        paddingY: 1,
                                        display: '-webkit-box',
                                        overflow: 'hidden',
                                        WebkitBoxOrient: 'vertical',
                                        WebkitLineClamp: 1, //numOfLines
                                    }}
                                    style={{
                                        ...pillTextStyle,
                                        borderRadius: '50px',
                                        opacity: maxSelectedDisableState && !isAlreadySelected(option) ? 0.5 : 1,
                                        textTransform: 'capitalize',
                                        fontFamily: isAlreadySelected(option)
                                            ? FONT_ENUM.MAIN_BOLD
                                            : FONT_ENUM.MAIN_MEDIUM,
                                        border: `1.0px solid ${theme[themeStyle].true_white}`,
                                        borderColor: isAlreadySelected(option)
                                            ? theme[themeStyle].primary_orange
                                            : theme[themeStyle].tag_white,
                                        color: isAlreadySelected(option)
                                            ? theme[themeStyle].black
                                            : theme[themeStyle].tag_white,
                                        backgroundColor: isAlreadySelected(option)
                                            ? theme[themeStyle].highlight
                                            : 'transparent',

                                        ...(isStep && {
                                            border: `1.0px solid ${theme[themeStyle].lightWhite}`,
                                            borderColor: isAlreadySelected(option)
                                                ? theme[themeStyle].primary_orange
                                                : theme[themeStyle].lightWhite,
                                            color: isAlreadySelected(option)
                                                ? theme[themeStyle].primary_white
                                                : theme[themeStyle].tag_white,
                                            backgroundColor: isAlreadySelected(option)
                                                ? theme[themeStyle].highlight
                                                : 'transparent',
                                        }),
                                    }}>
                                    {option}
                                </Typography>
                            </motion.div>
                        ))}
                </DialogContent>
                {step.instruction && (
                    <Typography
                        style={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: theme[themeStyle].true_white,
                            paddingLeft: '30px',
                        }}
                        sx={{
                            fontSize: {
                                xs: 12,
                                sm: 14,
                            },
                        }}>
                        {step.instruction}
                    </Typography>
                )}
                <div style={{ marginTop: 20, marginLeft: 30 }}>
                    {currentPage > 0 && (
                        <Button
                            size="small"
                            sx={{ color: theme[themeStyle].highlight, borderColor: theme[themeStyle].highlight }}
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 0))}
                            disabled={currentPage === 0}
                            style={{ position: 'fixed', ...prevButtonStyle }}>
                            <LottieAnimation
                                animationDirection={isMobile ? 'up' : 'left'}
                                animationData={arrowAnimationBlack}
                            />
                        </Button>
                    )}
                    {step && step.options && !step.hideArrow && (
                        <Button
                            size="small"
                            sx={{ color: theme[themeStyle].highlight, borderColor: theme[themeStyle].highlight }}
                            onClick={() => setCurrentPage(prev => (end < step.options!.length ? prev + 1 : prev))}
                            style={{ position: 'fixed', ...nextButtonStyle }}
                            disabled={end >= step.options.length}>
                            <LottieAnimation
                                animationDirection={isMobile ? 'down' : 'right'}
                                animationData={arrowAnimationBlack}
                            />
                        </Button>
                    )}
                </div>
                {shouldShowButton && (
                    <Button
                        variant="outlined"
                        size="large"
                        onClick={onClickButton}
                        disabled={disableButton}
                        sx={{ color: theme[themeStyle].highlight, borderColor: theme[themeStyle].highlight }}
                        style={{
                            borderRadius: 50,
                            fontFamily: FONT_ENUM.MEDIUM_HEADER,
                            paddingRight: 60,
                            paddingLeft: 60,
                            marginLeft: 30,
                            color: theme[themeStyle].true_white,
                            borderColor: theme[themeStyle].primary_white_orange,
                        }}>
                        {buttonText}
                    </Button>
                )}
            </motion.div>
        </AnimatePresence>
    );
};

export default TabSelector;
